import { adminRequest } from '@/shared/request-configs/admin-request';
import { IListingScorePayload } from '../interfaces/listing-score-payload';
import { IScoringRuleSet } from '../interfaces';
import { IListingScore } from '../interfaces/scoring-overall-result';

type TMissingScoreResponse = {
  status?: string;
};

const isMissingScoreResponse = (data: unknown): data is TMissingScoreResponse => (
  !!data
  && typeof data === 'object'
  && (data as TMissingScoreResponse).status === 'missing_score'
);

const getListingScoreSingleResponse = async (payload: IListingScorePayload) => adminRequest.get(
  `/v2/listing-scores-api/score/client/${payload.clientId}/property/${payload.propertyId}${payload.hideFullScores ? '?hideFullScores=true' : ''}`,
);

/**
 * Fetches Rules for Listing Scores <br>
 * API Endpoint - `/listing-scores-api/rules/${version}` <br> via {@link adminRequest.get}
 * @param {string} version - version of the rules
 */
export const getRules = async (version: string) => {
  const response = await adminRequest.get<IScoringRuleSet>(`/listing-scores-api/rules/${version}`);
  return response;
};
/**
 * Refreshes Listing Scores - Single <br>
 * API Endpoint - `/listing-scores-api/score/client/${payload.clientId}/property/${payload.propertyId}/refresh` <br> via {@link adminRequest.post}
 * @param {IListingScorePayload} payload - Listing Score for Data Manipulations
 */
export const refreshListingScoreSingle = async (payload: IListingScorePayload) => {
  const response = await adminRequest.post(`/v2/listing-scores-api/score/client/${payload.clientId}/property/${payload.propertyId}/refresh`, {});
  return response;
};

/**
 * Fetches Client Score Summary <br>
 * API Endpoint - `/listing-scores-api/score/client/${payload.clientId}/summary` <br> via {@link adminRequest.get}
 * @param {IListingScorePayload} payload - Listing Score for Data Manipulations
 */
export const getClientScoresSummary = async (payload: IListingScorePayload) => {
  const response = await adminRequest.get(`/v2/listing-scores-api/score/client/${payload.clientId}/summary`);
  return response;
};
/**
 * Fetches Score Summary in CSV <br>
 * API Endpoint - `/listing-scores-api/score/client/${payload.clientId}/csv` <br> via {@link adminRequest.get}
 * @param {IListingScorePayload} payload - Listing Score for Data Manipulations
 */
export const getScoreSummaryCSV = async (payload: IListingScorePayload) => {
  const response = await adminRequest.get(`/v2/listing-scores-api/score/client/${payload.clientId}/csv`);
  return response;
};

const sleep = async (ms: number): Promise<void> => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

/**
 * Polls getListingScoreSingle until evaluatedAt changes from the previous value,
 * indicating the consumer has re-scored after a data change.
 * @param {IListingScorePayload} payload - Listing Score payload
 * @param {string | Date | undefined} previousEvaluatedAt - The evaluatedAt value before the data change
 * @param {number} maxAttempts - Maximum number of poll attempts (default: 10)
 * @param {number} intervalMs - Milliseconds between poll attempts (default: 3000)
 * @returns {IListingScore | null} The updated score data, or null if polling timed out
 */
export const pollForUpdatedScore = async (
  payload: IListingScorePayload,
  previousEvaluatedAt: string | Date | undefined,
  maxAttempts = 10,
  intervalMs = 3000,
): Promise<IListingScore | null> => {
  const previousTime = previousEvaluatedAt ? new Date(previousEvaluatedAt).getTime() : 0;

  const poll = async (attempt: number): Promise<IListingScore | null> => {
    if (attempt >= maxAttempts) {
      return null;
    }

    await sleep(intervalMs);

    const response = await getListingScoreSingleResponse(payload);
    const result = isMissingScoreResponse(response.data) ? null : response.data as IListingScore;
    const evaluatedAtTime = result?.evaluatedAt
      ? new Date(result.evaluatedAt).getTime()
      : 0;

    if (result && evaluatedAtTime > previousTime) {
      return result;
    }

    return poll(attempt + 1);
  };

  return poll(0);
};

export const refreshAndPollListingScoreSingle = async (
  payload: IListingScorePayload,
  previousEvaluatedAt?: string | Date,
): Promise<IListingScore | null> => {
  await refreshListingScoreSingle(payload);
  return pollForUpdatedScore(payload, previousEvaluatedAt);
};

/**
 * Fetches Listing Score - Single <br>
 * API Endpoint - `/listing-scores-api/score/client/${payload.clientId}/property/${payload.propertyId}${payload.hideFullScores ? '?hideFullScores=true' : ''}` <br> via {@link adminRequest.get}
 * @param {IListingScorePayload} payload - Listing Score for Data Manipulations
 */
export const getListingScoreSingle = async (payload: IListingScorePayload) => {
  if (!payload.propertyId) return null;

  const response = await getListingScoreSingleResponse(payload);

  if (isMissingScoreResponse(response.data)) {
    return refreshAndPollListingScoreSingle(payload);
  }

  return response.data;
};

export default {
  getRules,
  refreshListingScoreSingle,
  getListingScoreSingle,
  getClientScoresSummary,
  getScoreSummaryCSV,
  pollForUpdatedScore,
  refreshAndPollListingScoreSingle,
};
